/**
 * ดันถอย (push) in the bout (data/duelPush.ts; kit audit 2026-10-13 piece 10): a push move that lands or is guarded drives
 * the one struck straight back from the striker, `hit` or `blocked` metres over `DUEL_PUSH.time`, against walls (the ring's
 * edge is open: past it the bout's ring-out rule counts). Nothing while the struck one is held from shoves (armour, a throw,
 * down: systems/duelHolds.ts) or either is in a lock form (the routine's own knock beats move it). In a bout's fight only.
 * Deterministic: no draws, time from the hooks' dt.
 */
import { DUEL_FLAGS } from '../data/duelMatch';
import { DUEL_PUSH, pushOf } from '../data/duelPush';
import type { Entity } from '../world';
import { shoveHeld } from './duelHolds';
import { duelLockOf } from './duelLock';
import type { SystemHost, WorldSystem } from './types';

const SYSTEMS = new WeakMap<object, DuelPushSystem>();
export const duelPushOf = (w: object): DuelPushSystem | null => SYSTEMS.get(w) ?? null;

export class DuelPushSystem implements WorldSystem {
  readonly name = 'duelPush';
  /** Fighter -> its push under way (velocity, seconds left). */
  private readonly pushes = new Map<number, { vx: number; vy: number; left: number }>();
  /** Counts for the tests and sims: pushes started, metres given. */
  readonly tally = { pushes: 0, metres: 0 };

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  setup(): void {
    const w = this.h.world;
    const prev = w.duelMod;
    w.duelMod = (src, tgt, res, kind, skillId) => {
      prev?.(src, tgt, res, kind, skillId);
      this.after(src, tgt, res.outcome, skillId);
    };
  }

  private inBout(id: number): boolean {
    const b = this.h.world.pvpBout;
    return DUEL_FLAGS.v2 && !!b && b.fight && (b.a === id || b.b === id);
  }

  private after(src: Entity, tgt: Entity, outcome: string, skillId: string): void {
    const p = pushOf(skillId);
    if (!p || src === tgt || !this.inBout(tgt.id)) return;
    const m =
      outcome === 'blocked'
        ? p.blocked
        : outcome === 'hit' || outcome === 'interrupt' || outcome === 'guardBreak' || outcome === 'guardShatter'
          ? p.hit
          : 0;
    if (m <= 0) return;
    const lock = duelLockOf(this.h.world);
    if (lock?.inForm(tgt.id) || lock?.inForm(src.id) || shoveHeld(this.h.world, tgt.id)) return;
    let dx = tgt.x - src.x;
    let dy = tgt.y - src.y;
    let l = Math.hypot(dx, dy);
    if (l < 1e-6) {
      dx = -tgt.dirX;
      dy = -tgt.dirY;
      l = Math.hypot(dx, dy) || 1;
    }
    this.pushes.set(tgt.id, { vx: ((dx / l) * m) / DUEL_PUSH.time, vy: ((dy / l) * m) / DUEL_PUSH.time, left: DUEL_PUSH.time });
    this.tally.pushes++;
    this.tally.metres += m;
  }

  step(dt: number): void {
    if (!this.pushes.size) return;
    const w = this.h.world;
    for (const [id, p] of [...this.pushes]) {
      const e = w.get(id);
      if (!e || e.dead || !this.inBout(id) || shoveHeld(w, id) || duelLockOf(w)?.inForm(id)) {
        this.pushes.delete(id);
        continue;
      }
      const s = Math.min(dt, p.left);
      const fx = e.dirX;
      const fy = e.dirY;
      this.h.move(e, p.vx * s, p.vy * s);
      // driven back, not turned round: keep the facing the move would have turned
      Object.assign(e, { dirX: fx, dirY: fy });
      p.left -= s;
      if (p.left <= 1e-9) this.pushes.delete(id);
    }
  }
}
